import * as THREE from "three";
import type { Rng } from "../rng";
import { FIRE } from "../config";
import { flat, Tone } from "../materials";
import { Slot, slotNormal } from "../palette";
import { blobGeometry, segmentGeometry } from "../geometry";

const FLAME = new THREE.MeshBasicMaterial({ color: 0xffffff });
const CORE = new THREE.MeshBasicMaterial({ color: 0xffffff });

const asFire = (mesh: THREE.Mesh, variant: number): THREE.Mesh => {
  mesh.layers.enable(1);
  mesh.userData.slot = slotNormal(Slot.fire);
  mesh.userData.variant = variant;
  return mesh;
};

export interface Fire {
  group: THREE.Group;
  update(time: number, daylight: number): void;
}

export function buildCampfire(rng: Rng): Fire {
  const group = new THREE.Group();

  const ash = new THREE.Mesh(new THREE.CircleGeometry(0.92, 16), flat(Tone.void));
  ash.rotation.x = -Math.PI / 2;
  ash.position.y = 0.012;
  ash.receiveShadow = true;
  ash.userData.slot = slotNormal(Slot.soil);
  ash.userData.variant = 0.5;
  group.add(ash);

  const stones = new THREE.Group();
  const count = rng.int(8, 11);
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + rng.range(-0.16, 0.16);
    const radius = rng.range(0.62, 0.78);
    const stone = blobGeometry(rng.range(0.11, 0.19), 1, 0.34, rng);
    const solid = new THREE.Mesh(stone, flat(rng.chance(0.5) ? Tone.stone : Tone.slate));
    solid.position.set(Math.cos(angle) * radius, rng.range(0.03, 0.09), Math.sin(angle) * radius);
    solid.rotation.set(rng.range(0, 1), rng.range(0, Math.PI * 2), rng.range(0, 1));
    solid.castShadow = true;
    solid.receiveShadow = true;
    solid.userData.slot = slotNormal(Slot.stone);
    solid.userData.variant = rng.range(0.2, 0.7);
    stones.add(solid);
  }
  group.add(stones);

  // teepee: each log spans from a foot on the ground up to just off the apex
  const logs = new THREE.Group();
  const logCount = rng.int(8, 10);
  const apex = 0.84;
  for (let i = 0; i < logCount; i++) {
    const angle = (i / logCount) * Math.PI * 2 + rng.range(-0.1, 0.1);
    const footRadius = rng.range(0.44, 0.54);
    const topRadius = rng.range(0.05, 0.13);
    const foot = new THREE.Vector3(
      Math.cos(angle) * footRadius,
      0.04,
      Math.sin(angle) * footRadius,
    );
    const top = new THREE.Vector3(
      Math.cos(angle) * topRadius,
      apex + rng.range(-0.06, 0.06),
      Math.sin(angle) * topRadius,
    );
    const radius = rng.range(0.05, 0.068);
    const log = new THREE.Mesh(
      segmentGeometry(foot, top, radius * 1.2, radius * 0.82, 6),
      flat(rng.chance(0.55) ? Tone.bark : Tone.ink),
    );
    log.castShadow = true;
    log.receiveShadow = true;
    log.userData.slot = slotNormal(Slot.wood);
    log.userData.variant = rng.range(0.3, 0.8);
    logs.add(log);

    // a shorter brace log leaning against the outer face
    if (i % 3 === 0) {
      const braceAngle = angle + 0.24;
      const braceFoot = new THREE.Vector3(
        Math.cos(braceAngle) * rng.range(0.62, 0.72),
        0.04,
        Math.sin(braceAngle) * rng.range(0.62, 0.72),
      );
      const braceTop = new THREE.Vector3(
        Math.cos(braceAngle) * 0.34,
        rng.range(0.42, 0.58),
        Math.sin(braceAngle) * 0.34,
      );
      const brace = new THREE.Mesh(
        segmentGeometry(braceFoot, braceTop, radius, radius * 0.8, 6),
        flat(Tone.bark),
      );
      brace.castShadow = true;
      brace.receiveShadow = true;
      brace.userData.slot = slotNormal(Slot.wood);
      brace.userData.variant = rng.range(0.2, 0.6);
      logs.add(brace);
    }
  }
  group.add(logs);

  // fire is built from blobs that rise, shrink and tumble rather than static
  // cones — four triangles read as four triangles however you light them
  interface Tongue {
    mesh: THREE.Mesh;
    seed: number;
    rate: number;
    angle: number;
    spread: number;
    rise: number;
    spin: number;
    base: number;
  }

  const tongues: Tongue[] = [];
  const COUNT = 13;
  for (let i = 0; i < COUNT; i++) {
    // hotter (paler) low in the fire, cooler (deeper) toward the tips
    const variant =
      i < 4 ? rng.range(0.0, 0.16) : i < 9 ? rng.range(0.28, 0.5) : rng.range(0.58, 0.88);
    const size = rng.range(0.1, 0.21) * (variant < 0.2 ? 1.15 : 1);
    const geometry = blobGeometry(size, 1, 0.45, rng);
    geometry.scale(1, rng.range(1.1, 1.6), 1);

    const mesh = asFire(new THREE.Mesh(geometry, variant < 0.2 ? CORE : FLAME), variant);
    mesh.geometry.computeBoundingSphere();
    group.add(mesh);

    tongues.push({
      mesh,
      seed: rng.next(),
      rate: rng.range(0.5, 0.95),
      angle: rng.range(0, Math.PI * 2),
      spread: rng.range(0.05, 0.2),
      rise: rng.range(1.15, 2.15),
      spin: rng.range(0.6, 2.2) * (rng.chance(0.5) ? 1 : -1),
      base: rng.range(0.66, 0.88),
    });
  }

  const coals: THREE.Mesh[] = [];
  const bed = new THREE.Group();
  for (let i = 0; i < 18; i++) {
    const angle = rng.range(0, Math.PI * 2);
    const radius = Math.sqrt(rng.next()) * 0.38;
    const size = rng.range(0.055, 0.12);
    const geometry = blobGeometry(size, 1, 0.5, rng);
    geometry.scale(1, rng.range(0.3, 0.5), 1);
    const coal = asFire(new THREE.Mesh(geometry, FLAME), rng.range(0.28, 0.72));
    coal.position.set(
      Math.cos(angle) * radius,
      rng.range(0.03, 0.12),
      Math.sin(angle) * radius,
    );
    coal.rotation.y = rng.range(0, Math.PI * 2);
    coal.userData.phase = rng.range(0, Math.PI * 2);
    bed.add(coal);
    coals.push(coal);
  }
  group.add(bed);

  const light = new THREE.PointLight(0xffa04a, 0, FIRE.lightDistance, 1.15);
  light.position.set(0, 1.0, 0);
  group.add(light);


  const update = (time: number, daylight: number): void => {
    const night = 1 - Math.min(1, Math.max(0, daylight));
    const base = FIRE.lightDay + (FIRE.lightNight - FIRE.lightDay) * night;

    let wobble = 0;
    tongues.forEach((tongue, index) => {
      const life = (time * tongue.rate + tongue.seed) % 1;
      const fade = Math.sin(life * Math.PI);
      const shrink = (0.45 + Math.sin(life * Math.PI) * 0.75) * (1 - life * 0.72);
      const curl = (1 - life * 0.55) * tongue.spread;

      tongue.mesh.position.set(
        Math.cos(tongue.angle) * curl + Math.sin(time * 3.1 + index) * 0.035,
        tongue.base + life * tongue.rise,
        Math.sin(tongue.angle) * curl + Math.cos(time * 2.7 + index) * 0.035,
      );
      tongue.mesh.scale.setScalar(shrink);
      tongue.mesh.rotation.set(life * 2.4, time * tongue.spin, life * 1.7 + index);

      tongue.mesh.visible = fade > 0.04;
      wobble += index < 4 ? fade * 0.06 : 0;
    });

    coals.forEach((coal) => {
      const phase = coal.userData.phase as number;
      const glow = 0.86 + Math.sin(time * 1.7 + phase) * 0.1 + Math.sin(time * 4.3 + phase * 1.7) * 0.06;
      coal.scale.setScalar(glow);
    });

    light.intensity = base * (1 + wobble * 1.6 + Math.sin(time * 17) * 0.05);
    light.distance = FIRE.lightDistance;
  };

  update(0, 1);

  return { group, update };
}
